import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { laboratorioService, Laboratorio, LaboratorioRequest, laboratorioDisciplinaService, turmaService, Turma, gradeService, Grade, agendaLaboratorioService, agendaLaboratorioHorarioService } from '../services/api';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import LaboratorioDisciplinaModal from './LaboratorioDisciplinaModal';

// Adicionando estilos para o efeito de hover sutil
const hoverStyle = `
  .btn-edit-laboratorio:hover {
    background-color: rgb(10, 88, 202) !important;
    border-color: rgb(10, 88, 202) !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(13, 110, 253, 0.3);
    transition: all 0.2s ease;
  }
`;

// Injetando o CSS no documento
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = hoverStyle;
  document.head.appendChild(styleElement);
}

const Laboratorios: React.FC = () => {
  const navigate = useNavigate();
  const [laboratorios, setLaboratorios] = useState<Laboratorio[]>([]);
  const [filteredLaboratorios, setFilteredLaboratorios] = useState<Laboratorio[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingLaboratorio, setEditingLaboratorio] = useState<Laboratorio | null>(null);
  const [selectedLaboratorioId, setSelectedLaboratorioId] = useState<string | null>(null);
  const [formData, setFormData] = useState<LaboratorioRequest>({
    nome: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    itemName: '',
    onConfirm: () => {},
    isLoading: false
  });

  // Estados para disciplinas do laboratório
  const [disciplinasLaboratorio, setDisciplinasLaboratorio] = useState<any[]>([]);
  const [loadingDisciplinas, setLoadingDisciplinas] = useState(false);
  const [showDisciplinaModal, setShowDisciplinaModal] = useState(false);
  const [disciplinaParaLaboratorio, setDisciplinaParaLaboratorio] = useState<{ id: string; nome: string; cargaHoraria: number } | null>(null);

  // Estado para turmas
  const [turmas, setTurmas] = useState<Turma[]>([]);

  // Estados para grades de laboratório
  const [gradesAula, setGradesAula] = useState<Grade[]>([]);
  const [gradesLaboratorio, setGradesLaboratorio] = useState<Grade[]>([]);
  const [loadingGrades, setLoadingGrades] = useState(false);
  const [generatingGrade, setGeneratingGrade] = useState(false);
  const [selectedGradeAulaId, setSelectedGradeAulaId] = useState<string>('');
  
  // Estado para controlar visibilidade da seção Grades de Laboratório
  const [showGradesSection, setShowGradesSection] = useState(true);

  // Estados para agendas de laboratório
  const [agendasLaboratorio, setAgendasLaboratorio] = useState<any[]>([]);
  const [loadingAgendas, setLoadingAgendas] = useState(false);
  const [selectedAgendaId, setSelectedAgendaId] = useState<string | null>(null);
  const [horariosAgenda, setHorariosAgenda] = useState<any[]>([]);
  const [loadingHorarios] = useState(false);

  useEffect(() => {
    loadLaboratorios();
    loadTurmas();
    loadGrades();
  }, []);

  const loadLaboratorios = async () => {
    try {
      setLoading(true);
      const data = await laboratorioService.getAll();
      setLaboratorios(data);
      setFilteredLaboratorios(data);
    } catch (error) {
      console.error('Erro ao carregar laboratórios:', error);
      setError('Erro ao carregar laboratórios');
    } finally {
      setLoading(false);
    }
  };

  const loadTurmas = async () => {
    try {
      const data = await turmaService.getAll();
      setTurmas(data);
    } catch (error) {
      console.error('Erro ao carregar turmas:', error);
    }
  };

  const loadGrades = async () => {
    try {
      setLoadingGrades(true);
      const gradesData = await gradeService.getAll(); // Carregar todas as grades
      setGradesAula(gradesData); // Usar todas as grades como opções para gerar laboratório
      const gradesLaboratorioData = await gradeService.listarGradesLaboratorio(); // Grades de laboratório geradas
      setGradesLaboratorio(gradesLaboratorioData);
    } catch (error) {
      console.error('Erro ao carregar grades:', error);
    } finally {
      setLoadingGrades(false);
    }
  };

  const handleGenerateGradeLaboratorio = async () => {
    if (!selectedGradeAulaId) {
      setError('Selecione uma grade para gerar a grade de laboratório');
      return;
    }

    try {
      setGeneratingGrade(true);
      setError('');
      await gradeService.gerarGradeLaboratorio(selectedGradeAulaId);
      setSuccess('Grade de laboratório gerada com sucesso!');
      await loadGrades(); // Recarregar grades
      setSelectedGradeAulaId('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Erro ao gerar grade de laboratório:', error);
      setError('Erro ao gerar grade de laboratório');
    } finally {
      setGeneratingGrade(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectLaboratorio = (laboratorioId: string) => {
    // Fechar formulário de novo laboratório ao selecionar um laboratório
    setShowForm(false);
    setSelectedLaboratorioId(laboratorioId);
    setError('');
    // Ocultar seção de Grades de Laboratório quando selecionar um laboratório
    setShowGradesSection(false);
    // Carregar agendas do laboratório selecionado
    loadAgendasLaboratorio(laboratorioId);
  };

  const handleBackToLaboratorios = () => {
    setSelectedLaboratorioId(null);
    setEditingLaboratorio(null);
    setShowForm(false);
    setError('');
    // Mostrar seção de Grades de Laboratório quando voltar à tela principal
    setShowGradesSection(true);
    // Limpar dados de agendas
    setAgendasLaboratorio([]);
    setSelectedAgendaId(null);
    setHorariosAgenda([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.nome.trim()) {
      setError('Nome é obrigatório');
      return;
    }

    try {
      setSaving(true);
      setError('');
      
      if (editingLaboratorio) {
        await laboratorioService.update(editingLaboratorio.id!, formData);
        setSuccess('Laboratório atualizado com sucesso!');
      } else {
        await laboratorioService.create(formData);
        setSuccess('Laboratório cadastrado com sucesso!');
      }
      
      await loadLaboratorios();
      resetForm();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Erro ao salvar laboratório:', error);
      setError('Erro ao salvar laboratório');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({ nome: '' });
    setEditingLaboratorio(null);
    setShowForm(false);
    setError('');
    // Mostrar seção de Grades de Laboratório quando cancelar formulário
    setShowGradesSection(true);
  };

  const handleEdit = (laboratorio: Laboratorio) => {
    setEditingLaboratorio(laboratorio);
    setFormData({ nome: laboratorio.nome });
    setShowForm(true);
    setError('');
  };

  const handleDelete = (laboratorio: Laboratorio) => {
    setDeleteModal({
      isOpen: true,
      title: 'Confirmar Exclusão',
      message: 'Tem certeza que deseja excluir este laboratório?',
      itemName: laboratorio.nome,
      onConfirm: () => confirmDelete(laboratorio.id!),
      isLoading: false
    });
  };

  const confirmDelete = async (id: string) => {
    try {
      setDeleteModal(prev => ({ ...prev, isLoading: true }));
      await laboratorioService.delete(id);
      await loadLaboratorios();
      setDeleteModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
      
      // Retornar automaticamente para a listagem de laboratórios após exclusão
      setSelectedLaboratorioId(null);
      
      setSuccess('Laboratório excluído com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Erro ao excluir laboratório:', error);
      setError('Erro ao excluir laboratório');
      setDeleteModal(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
    if (term.trim() === '') {
      setFilteredLaboratorios(laboratorios);
    } else {
      const filtered = laboratorios.filter(laboratorio =>
        laboratorio.nome.toLowerCase().includes(term.toLowerCase())
      );
      setFilteredLaboratorios(filtered);
    }
  }, [laboratorios]);

  // Funções para gerenciar disciplinas do laboratório
  const loadDisciplinasLaboratorio = useCallback(async (laboratorioId: string) => {
    try {
      setLoadingDisciplinas(true);
      const data = await laboratorioDisciplinaService.getByLaboratorio(laboratorioId);
      // Cruzar dados das disciplinas com as turmas
      const disciplinasComTurmas = data.map((disciplina: any) => {
        const turmaEncontrada = turmas.find(turma => turma.id === disciplina.disciplina?.turmaId);
        return {
          ...disciplina,
          disciplina: {
            ...disciplina.disciplina,
            turma: turmaEncontrada || disciplina.disciplina?.turma
          }
        };
      });
      setDisciplinasLaboratorio(disciplinasComTurmas);
    } catch (error) {
      console.error('Erro ao carregar disciplinas do laboratório:', error);
      setError('Erro ao carregar disciplinas do laboratório');
    } finally {
      setLoadingDisciplinas(false);
    }
  }, [turmas]);

  const handleEditarDisciplina = (disciplina: any) => {
    setDisciplinaParaLaboratorio({
      id: disciplina.disciplina.id,
      nome: disciplina.disciplina.nome,
      cargaHoraria: disciplina.disciplina.cargaHoraria || 1,
    });
    setShowDisciplinaModal(true);
  };

  const handleLaboratorioDisciplinaSucesso = () => {
    if (selectedLaboratorioId) {
      loadDisciplinasLaboratorio(selectedLaboratorioId);
    }
  };

  const handleExcluirDisciplina = (disciplina: any) => {
    setDeleteModal({
      isOpen: true,
      title: 'Confirmar Exclusão',
      message: 'Tem certeza que deseja excluir esta disciplina do laboratório?',
      itemName: disciplina.disciplina.nome,
      onConfirm: () => confirmDeleteDisciplina(disciplina.id),
      isLoading: false
    });
  };

  const confirmDeleteDisciplina = async (id: string) => {
    try {
      setDeleteModal(prev => ({ ...prev, isLoading: true }));
      await laboratorioDisciplinaService.delete(id);
      if (selectedLaboratorioId) {
        await loadDisciplinasLaboratorio(selectedLaboratorioId);
      }
      setDeleteModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
      setSuccess('Disciplina removida do laboratório com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Erro ao excluir disciplina do laboratório:', error);
      setError('Erro ao excluir disciplina do laboratório');
      setDeleteModal(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleDeleteGradeLaboratorio = (id: string) => {
    const grade = gradesLaboratorio.find(g => g.id === id);
    setDeleteModal({
      isOpen: true,
      title: 'Excluir Grade de Laboratório',
      message: 'Tem certeza que deseja excluir esta grade de laboratório?',
      itemName: grade?.nome || '',
      onConfirm: () => confirmDeleteGradeLaboratorio(id),
      isLoading: false
    });
  };

  const confirmDeleteGradeLaboratorio = async (id: string) => {
    setDeleteModal(prev => ({ ...prev, isLoading: true }));
    try {
      await gradeService.deleteGradeLaboratorio(id);
      setSuccess('Grade de laboratório excluída com sucesso!');
      loadGrades();
      setError('');
      setDeleteModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao excluir grade de laboratório');
      console.error('Erro ao excluir grade de laboratório:', err);
      setDeleteModal(prev => ({ ...prev, isLoading: false }));
    }
  };


  // Funções para agendas de laboratório
  const loadAgendasLaboratorio = async (laboratorioId: string) => {
    try {
      setLoadingAgendas(true);
      const data = await agendaLaboratorioService.getByLaboratorio(laboratorioId);
      setAgendasLaboratorio(data);
    } catch (error) {
      console.error('Erro ao carregar agendas do laboratório:', error);
      setError('Erro ao carregar agendas do laboratório');
    } finally {
      setLoadingAgendas(false);
    }
  };

  const handleBackToAgendas = () => {
    setSelectedAgendaId(null);
    setHorariosAgenda([]);
  };

  const toggleDisponibilidade = async (horarioId: string, disponivel: boolean) => {
    try {
      const updatedHorario = await agendaLaboratorioHorarioService.updateDisponibilidade(horarioId, { disponivel });
      
      // Atualizar o estado local
      setHorariosAgenda(prev => 
        prev.map(h => h.id === horarioId ? updatedHorario : h)
      );
      
      setSuccess('Status atualizado com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Erro ao atualizar disponibilidade:', error);
      setError('Erro ao atualizar disponibilidade');
    }
  };

  const toggleDiaDisponibilidade = async (diaSemana: number, disponivel?: boolean) => {
    const horariosDodia = horariosAgenda.filter(h => h.horario.diaSemana === diaSemana);
    
    if (horariosDodia.length === 0) return;
    
    // Se disponivel não foi especificado, determinar automaticamente
    // Se todos estão disponíveis, desativa todos. Caso contrário, ativa todos.
    const novoStatus = disponivel !== undefined ? disponivel : !horariosDodia.every(h => h.disponivel);
    
    try {
      const promises = horariosDodia.map(horario => 
        agendaLaboratorioHorarioService.updateDisponibilidade(horario.id, { disponivel: novoStatus })
      );
      
      await Promise.all(promises);
      
      // Atualizar o estado local
      setHorariosAgenda(prev => 
        prev.map(h => 
          h.horario.diaSemana === diaSemana 
            ? { ...h, disponivel: novoStatus } 
            : h
        )
      );
      
      setSuccess('Status do dia atualizado com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Erro ao atualizar disponibilidade do dia:', error);
      setError('Erro ao atualizar disponibilidade do dia');
    }
  };

  // Carregar disciplinas quando um laboratório é selecionado
  useEffect(() => {
    if (selectedLaboratorioId) {
      loadDisciplinasLaboratorio(selectedLaboratorioId);
    }
  }, [selectedLaboratorioId, loadDisciplinasLaboratorio]);

  useEffect(() => {
    handleSearch(searchTerm);
  }, [searchTerm, handleSearch]);

  return (
    <div className="main-content">
      <div className="container-fluid">
        {selectedLaboratorioId ? (
          // Área específica do laboratório selecionado
          <div>
            <div className="row mb-4">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center">
                  <h2 className="mb-0">
                    <i className="fas fa-flask me-2"></i>
                    {laboratorios.find(lab => lab.id === selectedLaboratorioId)?.nome}
                  </h2>
                  {selectedAgendaId ? (
                    <button className="btn btn-outline-secondary" onClick={handleBackToAgendas}>
                      <i className="fas fa-arrow-left me-2"></i>
                      Voltar para Agendas
                    </button>
                  ) : (
                    <button
                      className="btn btn-outline-secondary"
                      onClick={handleBackToLaboratorios}
                    >
                      <i className="fas fa-arrow-left me-2"></i>
                      Voltar
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Área de ações do laboratório */}
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      <i className="fas fa-flask me-2"></i>
                      Detalhes do Laboratório
                    </h5>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-success btn-edit-laboratorio"
                        style={{ 
                          backgroundColor: 'rgb(13, 110, 253)', 
                          borderColor: 'rgb(13, 110, 253)',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => {
                          const laboratorio = laboratorios.find(lab => lab.id === selectedLaboratorioId);
                          if (laboratorio) handleEdit(laboratorio);
                        }}
                      >
                        <i className="fas fa-edit me-2"></i>
                        Editar Laboratório
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => {
                          const laboratorio = laboratorios.find(lab => lab.id === selectedLaboratorioId);
                          if (laboratorio) handleDelete(laboratorio);
                        }}
                      >
                        <i className="fas fa-trash me-2"></i>
                        Excluir Laboratório
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-12">
                        <p><strong>Nome:</strong> {laboratorios.find(lab => lab.id === selectedLaboratorioId)?.nome}</p>
                      </div>
                    </div>
                    
                    {/* Seção de Agendas */}
                    <div className="mb-4 mt-4">
                      <h6 className="mb-3">
                        <i className="fas fa-calendar-alt me-2"></i>
                        Agendas do Laboratório
                      </h6>
                      {loadingAgendas ? (
                        <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Carregando agendas...</span>
                          </div>
                        </div>
                      ) : (
                        <div className="row">
                          {agendasLaboratorio.length === 0 ? (
                            <div className="col-12">
                              <div className="text-center py-5">
                                <i className="fas fa-calendar-times fa-3x text-muted mb-3"></i>
                                <h5 className="text-muted">Nenhuma agenda encontrada</h5>
                                <p className="text-muted">Este laboratório não possui agendas cadastradas.</p>
                              </div>
                            </div>
                          ) : (
                            agendasLaboratorio.map((agenda) => (
                              <div key={agenda.id} className="col-xl-4 col-lg-6 col-md-6 col-12 mb-4">
                                <div className="card h-100">
                                  <div className="card-body">
                                    <div className="d-flex align-items-center mb-3">
                                      <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                                        <i className="fas fa-calendar text-white"></i>
                                      </div>
                                      <div>
                                        <h6 className="card-title mb-0">{agenda.nome}</h6>
                                        <small className="text-muted">{agenda.periodo?.nome || 'Período não definido'}</small>
                                      </div>
                                    </div>
                                    <div className="d-grid">
                                      <button 
                                        className="btn btn-outline-primary"
                                        onClick={() => navigate(`/agenda-laboratorio/${selectedLaboratorioId}/${agenda.id}`)}
                                      >
                                        <i className="fas fa-cog me-2"></i>
                                        Gerenciar
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Formulário de edição do laboratório */}
            {showForm && editingLaboratorio && (
              <div className="row mt-4">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h5 className="mb-0">
                        <i className="fas fa-edit me-2"></i>
                        Editar Laboratório
                      </h5>
                    </div>
                    <div className="card-body p-4">
                      <form onSubmit={handleSubmit}>
                        <div className="row mb-4">
                          <div className="col-md-6 mb-3">
                            <label htmlFor="nome" className="form-label fw-semibold">Nome do Laboratório *</label>
                            <input
                              type="text"
                              className="form-control form-control-lg"
                              id="nome"
                              name="nome"
                              value={formData.nome}
                              onChange={handleInputChange}
                              placeholder="Ex: Laboratório de Informática, Lab. de Química..."
                              maxLength={50}
                              style={{
                                borderRadius: '8px',
                                border: '2px solid #e9ecef',
                                transition: 'all 0.2s ease'
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = '#007bff';
                                e.target.style.boxShadow = '0 0 0 0.2rem rgba(0, 123, 255, 0.25)';
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = '#e9ecef';
                                e.target.style.boxShadow = 'none';
                              }}
                              required
                            />
                            <div className="d-flex gap-3 justify-content-end mt-3">
                              <button
                                type="button"
                                className="btn btn-outline-secondary px-4 py-2"
                                onClick={resetForm}
                                disabled={saving}
                                style={{
                                  borderRadius: '8px',
                                  fontWeight: '500',
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <i className="fas fa-times me-2"></i>
                                Cancelar
                              </button>
                              <button
                                type="submit"
                                className="btn btn-primary px-4 py-2"
                                disabled={saving}
                                style={{
                                  borderRadius: '8px',
                                  fontWeight: '500',
                                  transition: 'all 0.2s ease',
                                  minWidth: '120px'
                                }}
                              >
                                {saving ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Atualizando...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-save me-2"></i>
                                    Atualizar
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Mensagens de erro e sucesso */}
            {error && (
              <div className="alert alert-danger alert-dismissible fade show mt-3" role="alert">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {error}
                <button type="button" className="btn-close" onClick={() => setError('')}></button>
              </div>
            )}

            {success && (
              <div className="alert alert-success alert-dismissible fade show mt-3" role="alert">
                <i className="fas fa-check-circle me-2"></i>
                {success}
                <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
              </div>
            )}

            {/* Seção de Disciplinas do Laboratório */}
            <div className="row mt-4">
              <div className="col-12">
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      <i className="fas fa-book me-2"></i>
                      Disciplinas Vinculadas
                    </h5>
                    <div className="d-flex gap-3">
                      <div className="badge bg-primary d-flex align-items-center fs-6 px-3 py-2">
                        <i className="fas fa-list-ol me-2"></i>
                        Total: {disciplinasLaboratorio.length}
                      </div>
                      {(() => {
                        // Calcular totais por período
                        const totaisPorPeriodo = disciplinasLaboratorio.reduce((acc: any, item: any) => {
                          const periodoNome = item.disciplina?.turma?.periodo?.nome || 'Período não definido';
                          if (!acc[periodoNome]) {
                            acc[periodoNome] = 0;
                          }
                          acc[periodoNome] += item.quantidade || 0;
                          return acc;
                        }, {});

                        return Object.entries(totaisPorPeriodo).map(([periodo, total]) => (
                          <div key={periodo} className="badge bg-success d-flex align-items-center fs-6 px-3 py-2">
                            <i className="fas fa-calendar-alt me-2"></i>
                            {periodo}: {total as number} aula{(total as number) > 1 ? 's' : ''}
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                  <div className="card-body">
                    {loadingDisciplinas ? (
                      <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Carregando disciplinas...</span>
                        </div>
                      </div>
                    ) : disciplinasLaboratorio.length === 0 ? (
                      <div className="text-center py-5">
                        <i className="fas fa-book-open fa-3x text-muted mb-3"></i>
                        <h5 className="text-muted">Nenhuma disciplina vinculada</h5>
                        <p className="text-muted">Este laboratório ainda não possui disciplinas vinculadas.</p>
                      </div>
                    ) : (
                      <div 
                        className="table-responsive" 
                        style={{
                          maxHeight: '400px',
                          overflowY: 'auto',
                          border: '1px solid #dee2e6',
                          borderRadius: '0.375rem'
                        }}
                      >
                        <table className="table table-striped table-hover mb-0">
                          <thead className="table-dark sticky-top">
                            <tr>
                              <th>Disciplina</th>
                              <th>Professor</th>
                              <th>Turma</th>
                              <th>Período</th>
                              <th>Quantidade</th>
                              <th>Modo</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                           <tbody>
                             {disciplinasLaboratorio.map((item: any) => (
                               <tr key={item.id}>
                                 <td>{item.disciplina?.nome || 'Nome não definido'}</td>
                                 <td>{item.disciplina?.professor?.nome || 'Professor não definido'}{item.disciplina?.professor2 ? ` e ${item.disciplina.professor2.nome}` : ''}</td>
                                 <td>{item.disciplina?.turma?.nome || 'Turma não definida'}</td>
                                 <td>{item.disciplina?.turma?.periodo?.nome || 'Período não definido'}</td>
                                 <td>
                                   <span className="badge bg-secondary">
                                     {item.quantidade} aula{item.quantidade > 1 ? 's' : ''}
                                   </span>
                                 </td>
                                 <td>
                                   {item.modo === 'EXCLUSIVO' ? (
                                     <span className="badge bg-danger">
                                       <i className="fas fa-lock me-1"></i>
                                       Exclusivo
                                     </span>
                                   ) : item.modo === 'PREFERENCIAL' ? (
                                     <span className="badge bg-success">
                                       <i className="fas fa-star me-1"></i>
                                       Preferencial
                                     </span>
                                   ) : (
                                     <span className="badge bg-light text-dark">
                                       Qualquer
                                     </span>
                                   )}
                                 </td>
                                 <td>
                                   <div className="btn-group" role="group">
                                     <button
                                       className="btn btn-outline-primary btn-sm"
                                       onClick={() => handleEditarDisciplina(item)}
                                       title="Editar disciplina"
                                     >
                                       <i className="fas fa-edit"></i>
                                     </button>
                                     <button
                                       className="btn btn-outline-danger btn-sm"
                                       onClick={() => handleExcluirDisciplina(item)}
                                       title="Remover disciplina"
                                     >
                                       <i className="fas fa-trash"></i>
                                     </button>
                                   </div>
                                 </td>
                               </tr>
                             ))}
                           </tbody>
                         </table>
                       </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {disciplinaParaLaboratorio && (
              <LaboratorioDisciplinaModal
                isOpen={showDisciplinaModal}
                onClose={() => setShowDisciplinaModal(false)}
                disciplina={disciplinaParaLaboratorio}
                laboratorios={laboratorios}
                onSucesso={handleLaboratorioDisciplinaSucesso}
              />
            )}

            {/* Tela de Gerenciamento de Horários da Agenda */}
            {selectedAgendaId && (
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h5 className="mb-0">
                        <i className="fas fa-clock me-2"></i>
                        Gerenciar Horários da Agenda
                      </h5>
                    </div>
                    <div className="card-body">
                      
                      <div className="alert alert-info mb-4">
                        <div className="row">
                          <div className="col-md-6">
                            <strong>
                              <i className="fas fa-flask me-2"></i>
                              Laboratório:
                            </strong>
                            <span className="ms-2">
                              {laboratorios.find(l => l.id === selectedLaboratorioId)?.nome || 'Não identificado'}
                            </span>
                          </div>
                          <div className="col-md-6">
                            <strong>
                              <i className="fas fa-calendar me-2"></i>
                              Período:
                            </strong>
                            <span className="ms-2">
                              {agendasLaboratorio.find(a => a.id === selectedAgendaId)?.periodo?.nome || 'Não identificado'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {loadingHorarios ? (
                        <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Carregando horários...</span>
                          </div>
                        </div>
                      ) : (
                        <div>
                          {horariosAgenda.length === 0 ? (
                            <div className="text-center py-5">
                              <i className="fas fa-clock fa-3x text-muted mb-3"></i>
                              <h5 className="text-muted">Nenhum horário encontrado</h5>
                              <p className="text-muted">Esta agenda não possui horários cadastrados.</p>
                            </div>
                          ) : (
                            <div className="row">
                              {/* Colunas dos dias da semana */}
                              {[1, 2, 3, 4, 5, 6, 0].map(diaSemana => {
                                const diasNomes = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
                                const horariosDay = horariosAgenda.filter(h => h.horario.diaSemana === diaSemana);
                                
                                if (horariosDay.length === 0) return null;
                                
                                return (
                                  <div key={diaSemana} className="col-lg-2 col-md-4 col-sm-6 mb-4">
                                    <div className="card" style={{ minHeight: '400px' }}>
                                      <div 
                                        className="card-header bg-primary text-white text-center" 
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => toggleDiaDisponibilidade(diaSemana)}
                                        title="Clique para inverter a disponibilidade de todos os horários deste dia"
                                      >
                                        <h6 className="mb-0">{diasNomes[diaSemana]}</h6>
                                        <small style={{ fontSize: '0.7em', opacity: 0.8 }}>Clique para inverter</small>
                                      </div>
                                      <div className="card-body p-2">
                                        {horariosDay
                                          .sort((a, b) => (a.horario?.inicio || '').localeCompare(b.horario?.inicio || ''))
                                          .map((horario) => (
                                            <div 
                                              key={horario.id} 
                                              className={`card mb-2 ${horario.disponivel ? 'border-success' : 'border-danger'}`}
                                              style={{ cursor: 'pointer' }}
                                              onClick={() => toggleDisponibilidade(horario.id, !horario.disponivel)}
                                            >
                                              <div className="card-body p-2 text-center">
                                                <div className={`mb-1 ${horario.disponivel ? 'text-success' : 'text-danger'}`}>
                                                  <i className={`fas ${horario.disponivel ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                                                </div>
                                                <small className="d-block">
                                                  <strong>{horario.horario?.inicio || '--:--'} - {horario.horario?.fim || '--:--'}</strong>
                                                </small>
                                                <span className={`badge ${horario.disponivel ? 'bg-success' : 'bg-danger'} mt-1`} style={{ fontSize: '0.7em' }}>
                                                  {horario.disponivel ? 'Disponível' : 'Indisponível'}
                                                </span>
                                              </div>
                                            </div>
                                          ))}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          // Lista de laboratórios
          <div>
            <div className="row">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-start mb-4">
                  <div className="flex-grow-1">
                    <h2 className="mb-2 text-start">
                      <i className="fas fa-flask me-2"></i>
                      Laboratórios
                    </h2>
                  </div>
                  <button
                    className="btn btn-success"
                    onClick={() => {
                      const newShowForm = !showForm;
                      setShowForm(newShowForm);
                      // Ocultar seção de Grades de Laboratório quando abrir formulário de novo laboratório
                      if (newShowForm) {
                        setShowGradesSection(false);
                      } else {
                        // Mostrar seção de Grades de Laboratório quando fechar formulário
                        setShowGradesSection(true);
                      }
                    }}
                  >
                    <i className="fas fa-plus me-2"></i>
                    {showForm ? 'Cancelar' : 'Novo Laboratório'}
                  </button>
                </div>
              </div>
            </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <button type="button" className="btn-close" onClick={() => setError('')}></button>
          </div>
        )}

        {success && (
          <div className="alert alert-success alert-dismissible fade show" role="alert">
            <i className="fas fa-check-circle me-2"></i>
            {success}
            <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
          </div>
        )}

        {showForm && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">
                    <i className="fas fa-flask me-2"></i>
                    {editingLaboratorio ? 'Editar Laboratório' : 'Cadastrar Novo Laboratório'}
                  </h5>
                </div>
                <div className="card-body p-4">
                  <form onSubmit={handleSubmit}>
                    <div className="row mb-4">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="nome" className="form-label fw-semibold">Nome do Laboratório *</label>
                        <input
                          type="text"
                          className="form-control form-control-lg"
                          id="nome"
                          name="nome"
                          value={formData.nome}
                          onChange={handleInputChange}
                          placeholder="Ex: Laboratório de Informática, Lab. de Química..."
                          maxLength={50}
                          style={{
                            borderRadius: '8px',
                            border: '2px solid #e9ecef',
                            transition: 'all 0.2s ease'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#007bff';
                            e.target.style.boxShadow = '0 0 0 0.2rem rgba(0, 123, 255, 0.25)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e9ecef';
                            e.target.style.boxShadow = 'none';
                          }}
                          required
                        />
                        <div className="d-flex gap-3 justify-content-end mt-3">
                          <button
                            type="button"
                            className="btn btn-outline-secondary px-4 py-2"
                            onClick={resetForm}
                            disabled={saving}
                            style={{
                              borderRadius: '8px',
                              fontWeight: '500',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <i className="fas fa-times me-2"></i>
                            Cancelar
                          </button>
                          <button
                            type="submit"
                            className="btn btn-primary px-4 py-2"
                            disabled={saving}
                            style={{
                              borderRadius: '8px',
                              fontWeight: '500',
                              transition: 'all 0.2s ease',
                              minWidth: '120px'
                            }}
                          >
                            {saving ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                {editingLaboratorio ? 'Atualizando...' : 'Cadastrando...'}
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save me-2"></i>
                                {editingLaboratorio ? 'Atualizar' : 'Cadastrar'}
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Lista de Laboratórios */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <h5 className="mb-0">
                      <i className="fas fa-list me-2"></i>
                      Laboratórios Cadastrados
                    </h5>
                  </div>
                  <div className="col-md-6">
                    <div className="input-group" style={{ width: '300px', marginLeft: 'auto' }}>
                      <span className="input-group-text">
                        <i className="fas fa-search"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Buscar laboratório..."
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                        maxLength={30}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-body">
                {loading ? (
                  <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Carregando laboratórios...</span>
                    </div>
                  </div>
                ) : (
                  <div className="row">
                    {filteredLaboratorios.length === 0 ? (
                      <div className="col-12">
                        <div className="text-center py-5">
                          <i className="fas fa-flask fa-3x text-muted mb-3"></i>
                          <h5 className="text-muted">Nenhum laboratório encontrado</h5>
                          <p className="text-muted">
                            {searchTerm ? 'Tente ajustar os termos de busca.' : 'Comece cadastrando um novo laboratório.'}
                          </p>
                        </div>
                      </div>
                    ) : (
                      filteredLaboratorios.map((laboratorio) => (
                        <div key={laboratorio.id} className="col-xl-3 col-lg-4 col-md-6 col-12 mb-3">
                          <div className="turma-card" style={{ cursor: 'pointer' }} onClick={() => handleSelectLaboratorio(laboratorio.id!)}>
                            <div className="d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <div className="bg-success rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '50px', height: '50px' }}>
                                  <i className="fas fa-flask text-white"></i>
                                </div>
                                <div>
                                  <h6 className="mb-0">{laboratorio.nome}</h6>
                                  <small className="text-muted">
                                    {selectedLaboratorioId === laboratorio.id ? (
                                      <>
                                        <i className="fas fa-check me-1"></i>
                                        Selecionado
                                      </>
                                    ) : (
                                      'Clique para gerenciar'
                                    )}
                                  </small>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
          </div>
        )}

        {/* Seção de Grades de Laboratório - só exibe quando showGradesSection for true */}
        {showGradesSection && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-header bg-light">
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <h5 className="mb-0">
                      <i className="fas fa-calendar-alt me-2"></i>
                      Grades de Laboratório
                    </h5>
                  </div>
                </div>
              </div>
              <div className="card-body">
                {/* Seção de Geração de Grade */}
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="card border-0 shadow-sm">
                      <div className="card-header bg-light">
                        <h6 className="mb-0 text-dark fw-semibold">
                          <i className="fas fa-plus-circle me-2 text-primary"></i>
                          Gerar Nova Grade de Laboratório
                        </h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          {/* Coluna Esquerda - Seleção da Grade */}
                          <div className="col-md-8">
                            <div className="bg-light rounded-3 p-4 h-100">
                              <div className="d-flex align-items-center mb-4">
                                <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                                  <i className="fas fa-calendar-alt text-white"></i>
                                </div>
                                <h5 className="mb-0 text-primary fw-bold">Selecionar Grade Base</h5>
                              </div>
                              
                              <div className="mb-3">
                                <label htmlFor="gradeAulaSelect" className="form-label fw-semibold text-dark mb-2">
                                  Grade de Aula *
                                </label>
                                <select
                                  id="gradeAulaSelect"
                                  className="form-select form-select-lg"
                                  value={selectedGradeAulaId}
                                  onChange={(e) => setSelectedGradeAulaId(e.target.value)}
                                  disabled={generatingGrade}
                                  style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                                >
                                  <option value="">Selecione uma grade...</option>
                                  {gradesAula.map((grade) => (
                                    <option key={grade.id} value={grade.id}>
                                      {grade.nome} {grade.data && `- ${new Date(grade.data).toLocaleDateString('pt-BR')}`}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              
                              {selectedGradeAulaId && (
                                <div className="alert alert-info border-0" style={{ backgroundColor: '#e3f2fd', borderRadius: '10px' }}>
                                  <i className="fas fa-info-circle me-2"></i>
                                  <small>A grade de laboratório será gerada com base na grade selecionada.</small>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Coluna Direita - Ação de Geração */}
                          <div className="col-md-4">
                            <div className="bg-light rounded-3 p-4 h-100 d-flex flex-column">
                              <div className="d-flex align-items-center mb-4">
                                <div className="bg-success rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                                  <i className="fas fa-cogs text-white"></i>
                                </div>
                                <h5 className="mb-0 text-success fw-bold">Gerar Grade</h5>
                              </div>
                              
                              <div className="flex-grow-1 d-flex flex-column justify-content-center">
                                <button
                                  type="button"
                                  className="btn btn-success btn-lg w-100 mb-3"
                                  onClick={handleGenerateGradeLaboratorio}
                                  disabled={generatingGrade || !selectedGradeAulaId}
                                  style={{ borderRadius: '10px', padding: '12px' }}
                                >
                                  {generatingGrade ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                      Gerando...
                                    </>
                                  ) : (
                                    <>
                                      <i className="fas fa-magic me-2"></i>
                                      Gerar Grade
                                    </>
                                  )}
                                </button>
                                
                                <small className="text-muted text-center">
                                  <i className="fas fa-lightbulb me-1"></i>
                                  Processo automático baseado na grade selecionada
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Lista de Grades de Laboratório */}
                <div className="row">
                  <div className="col-12">
                    <h6 className="mb-3">
                      <i className="fas fa-list me-2"></i>
                      Grades de Laboratório Geradas
                    </h6>
                    {loadingGrades ? (
                      <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Carregando grades...</span>
                        </div>
                      </div>
                    ) : gradesLaboratorio.length === 0 ? (
                      <div className="text-center py-5">
                        <i className="fas fa-calendar-times fa-3x text-muted mb-3"></i>
                        <h5 className="text-muted">Nenhuma grade de laboratório encontrada</h5>
                        <p className="text-muted">
                           Gere uma nova grade de laboratório selecionando uma grade base.
                         </p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-hover">
                          <thead className="table-light">
                            <tr>
                              <th>Nome da Grade</th>
                              <th style={{width: '120px'}}>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {gradesLaboratorio.map((grade) => (
                              <tr key={grade.id}>
                                <td 
                                  onClick={() => window.location.href = `/grade-laboratorio/${grade.id}`}
                                  style={{ cursor: 'pointer' }}
                                  title="Clique para visualizar a grade de laboratório"
                                >
                                  <strong>{grade.nome}</strong>
                                  {grade.data && (
                                    <div className="text-muted small">
                                      Gerada em: {new Date(grade.data).toLocaleString('pt-BR')}
                                    </div>
                                  )}
                                </td>
                                <td>
                                  <div className="btn-group btn-group-sm" role="group">
                                    <button
                                      className="btn btn-outline-info"
                                      onClick={() => window.location.href = `/grade-laboratorio/${grade.id}`}
                                      title="Visualizar Grade de Laboratório"
                                    >
                                      <i className="fas fa-eye"></i>
                                    </button>
                                    <button
                                      className="btn btn-outline-danger"
                                      onClick={() => handleDeleteGradeLaboratorio(grade.id!)}
                                      title="Excluir Grade de Laboratório"
                                    >
                                      <i className="fas fa-trash"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Modal de Confirmação */}
        <DeleteConfirmationModal
          isOpen={deleteModal.isOpen}
          title={deleteModal.title}
          message={deleteModal.message}
          itemName={deleteModal.itemName}
          onConfirm={deleteModal.onConfirm}
          onClose={() => setDeleteModal((prev: any) => ({ ...prev, isOpen: false }))}
          isLoading={deleteModal.isLoading}
        />
      </div>
    </div>
  );
};

export default Laboratorios;